import * as React from "react";

import { cn } from "@/shared/lib/utils";

import { ColocaSkeleton } from "../feedback/ColocaSkeleton";
import { colocaSurfaceVariants } from "../layout/colocaSurfaceVariants";

export interface ColocaDataTableColumn<TRow> {
  /** Stable column identifier — used as the React key for header and cells. */
  id: string;
  /** Column header content. */
  header: React.ReactNode;
  /** Renders the cell for a row. Keep cells presentational; derive data before passing rows in. */
  cell: (row: TRow) => React.ReactNode;
  /** Extra classes for the `<th>` — e.g. a fixed width or `text-right`. */
  headerClassName?: string;
  /** Extra classes for every `<td>` in this column. */
  cellClassName?: string;
}

export interface ColocaDataTableProps<TRow> extends React.ComponentPropsWithoutRef<"div"> {
  /** Ordered column definitions. */
  columns: ColocaDataTableColumn<TRow>[];
  /** Page of rows to render. Pagination/sorting happen outside (usually server-side). */
  rows: TRow[];
  /** Returns a stable key for a row — typically the entity id. */
  rowKey: (row: TRow) => string;
  /**
   * When set, rows become interactive: pointer cursor, hover highlight, and keyboard activation (Enter /
   * Space). Use for drill-down navigation; keep per-cell actions on buttons inside cells — clicks on
   * interactive elements inside a row do not trigger the row click.
   */
  onRowClick?: (row: TRow) => void;
  /** Shows a skeleton body instead of rows. */
  loading?: boolean;
  /** Number of skeleton rows rendered while loading. @default 6 */
  skeletonRows?: number;
  /** Rendered in place of the body when there are no rows and `loading` is false — pass a `ColocaEmpty`. */
  empty?: React.ReactNode;
  /**
   * Footer slot below the table — typically a `ColocaPagination`.
   *
   * The slot owns the chrome: it draws the rule that separates the footer from the last row, and the padding
   * that insets it. So what you pass is CONTENT, and it must not draw a boundary of its own — a
   * `ColocaPagination` here needs `withSeparator={false}`, or the two rules render 12px apart.
   */
  footer?: React.ReactNode;
  /**
   * Extra classes for the body `<tr>`s — a string applies to every row, a function derives them per row (e.g.
   * to dim superseded versions or tint an error row).
   */
  rowClassName?: string | ((row: TRow) => string | undefined);
  /**
   * Card chrome around the table. `"card"` draws the standard `ColocaSurface` panel; `"none"` draws no
   * border, radius or background — pass it whenever the host already provides a surface, or the two frames
   * stack into a doubled hairline and the inner radius is clipped by the outer one, notching every corner.
   *
   * @default "card"
   */
  frame?: "card" | "none";
  /**
   * Width in px below which the table scrolls horizontally instead of crushing its columns. Derive it from
   * the sum of the columns' declared widths: `max-width` on a `<td>` is only a hint to the auto table layout,
   * so without a floor the columns compress silently and then overflow with no affordance.
   */
  minWidth?: number;
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("button, a, input, select, label, [role='checkbox'], [role='menuitem']") !== null
  );
}

/**
 * Standard data table for list pages: hairline row separators, an opaque header rule, hover highlight, and
 * built-in loading / empty states. Columns are declarative — each one owns its header and cell renderer — so
 * module tables stay thin mappers from API rows to cells.
 *
 * The table is presentation-only: pagination, sorting, and filtering live with the caller (server-side in
 * most modules). Pass a `ColocaPagination` through `footer` and a `ColocaEmpty` through `empty`.
 *
 * Two rules worth knowing before you use it. It draws a card of its own, so pass `frame="none"` when the host
 * already drew one. And past five columns it needs a `minWidth`, below which it scrolls horizontally — the
 * scroll region becomes focusable and a right-edge fade signals there is more, so a clipped column never
 * reads as a rendering bug.
 *
 * The header is deliberately NOT sticky: sticking it needs a height-capped scroll container, which nests a
 * second scrollbar inside the page scroller (the mistake `ColocaDialog` documents). These lists are
 * server-paginated to a page at a time.
 *
 * @summary declarative table with overflow affordance, skeleton loading, and empty/footer slots
 */
export function ColocaDataTable<TRow>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  skeletonRows = 6,
  empty,
  footer,
  rowClassName,
  frame = "card",
  minWidth,
  className,
  ...rest
}: ColocaDataTableProps<TRow>) {
  const showEmpty = !loading && rows.length === 0;
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  // The same measurement answers two questions: whether the scroll region needs to be keyboard-reachable
  // (WCAG 2.1.1 — it has no other operable child) and whether to fade the cut edge.
  React.useEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport) return;
    const measure = () => setIsOverflowing(viewport.scrollWidth > viewport.clientWidth + 1);
    measure();
    // BOTH boxes, because overflow is a relation between them and either side can move on its own. The
    // viewport answers "did the space change" (sidebar collapse, window resize); the table answers "did the
    // content change" — a longer translated header or a wider value arriving from a poll grows the table
    // inside a viewport that never resizes, and observing only the viewport would leave the state stale.
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    if (tableRef.current) observer.observe(tableRef.current);
    return () => observer.disconnect();
  }, [columns.length, rows.length, loading, minWidth]);

  return (
    <div
      className={cn(
        "overflow-hidden",
        frame === "card" && colocaSurfaceVariants({ variant: "panel", padding: "none" }),
        className,
      )}
      {...rest}
    >
      {!showEmpty && (
        <div className="relative">
          <div
            ref={scrollRef}
            tabIndex={isOverflowing ? 0 : undefined}
            className="custom-scrollbar overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
          >
            <table ref={tableRef} className="w-full text-sm" style={minWidth ? { minWidth } : undefined}>
              {/* The rule lives on the `thead`, not on its `tr`: a translucent, stacking-context header paints
                its own background over a collapsed border and the separator disappears. */}
              <thead className="border-b border-border bg-card">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.id}
                      scope="col"
                      className={cn(
                        "h-10 whitespace-nowrap px-4 text-left text-xs font-medium text-muted-foreground",
                        column.headerClassName,
                      )}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: skeletonRows }, (_, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-border/50 last:border-b-0">
                        {columns.map((column) => (
                          <td key={column.id} className={cn("h-13 px-4", column.cellClassName)}>
                            <ColocaSkeleton className="h-4 w-3/4 max-w-40" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : rows.map((row) => (
                      <tr
                        key={rowKey(row)}
                        tabIndex={onRowClick ? 0 : undefined}
                        onClick={
                          onRowClick
                            ? (event) => {
                                if (!isInteractiveTarget(event.target)) onRowClick(row);
                              }
                            : undefined
                        }
                        onKeyDown={
                          onRowClick
                            ? (event) => {
                                if (
                                  (event.key === "Enter" || event.key === " ") &&
                                  event.target === event.currentTarget
                                ) {
                                  event.preventDefault();
                                  onRowClick(row);
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          "group/row border-b border-border/50 transition-colors last:border-b-0",
                          // The ring is drawn INWARD: the root clips at its radius, so an outward one would be
                          // eaten on the first and last row.
                          onRowClick &&
                            "cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                          typeof rowClassName === "function" ? rowClassName(row) : rowClassName,
                        )}
                      >
                        {columns.map((column) => (
                          <td key={column.id} className={cn("h-13 px-4", column.cellClassName)}>
                            {column.cell(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
          {/* A column cut off mid-glyph reads as a rendering bug; a soft edge reads as "there is more". */}
          {isOverflowing && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent"
            />
          )}
        </div>
      )}
      {showEmpty && empty}
      {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
    </div>
  );
}
